import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { map, catchError } from 'rxjs';
import { FlaggedContactHeaderModel } from '../models/flagged-contact-header.model';
import { FlaggedContactSubContactModel } from '../models/flagged-contact-sub-contact.model';
import { AuditModel } from '../models/audit.model';
import { environment } from '../../environments/environment';
import { MockDataHelper } from '../helpers/mock-data.helper';
import { OfacMaintenanceClient, FlaggedContactModel, FlaggedContactDetailModel, AuditDetailModel, OfacAuditModel, OfacExclusionModel, ServiceBusModel } from './sdk';

@Injectable({
  providedIn: 'root'
})
export class OfacRecordsService {
  constructor(private sdk: OfacMaintenanceClient) {}

  /**
   * Helper method to get property value from object, handling both camelCase and PascalCase property names
   */
  private getProperty(obj: any, ...keys: string[]): any {
    for (const key of keys) {
      if (obj && (key in obj)) {
        return obj[key];
      }
    }
    return null;
  }

  /**
   * Sorts header records by lastName, firstName, middleNames
   */
  private sortHeaderRecords(records: FlaggedContactHeaderModel[]): FlaggedContactHeaderModel[] {
    return records.sort((a, b) => {
      // Sort by lastName first
      const lastNameCompare = (a.lastName || '').localeCompare(b.lastName || '');
      if (lastNameCompare !== 0) {
        return lastNameCompare;
      }
      
      // If lastNames are equal, sort by firstName
      const firstNameCompare = (a.firstName || '').localeCompare(b.firstName || '');
      if (firstNameCompare !== 0) {
        return firstNameCompare;
      }
      
      // If both lastName and firstName are equal, sort by middleNames
      return (a.middleNames || '').localeCompare(b.middleNames || '');
    });
  }

  /**
   * Sorts sub contact records by lastName, firstName, middleNames
   */
  private sortSubRecords(records: FlaggedContactSubContactModel[]): FlaggedContactSubContactModel[] {
    return records.sort((a, b) => {
      // Sort by lastName first
      const lastNameCompare = (a.lastName || '').localeCompare(b.lastName || '');
      if (lastNameCompare !== 0) {
        return lastNameCompare;
      }
      
      // If lastNames are equal, sort by firstName
      const firstNameCompare = (a.firstName || '').localeCompare(b.firstName || '');
      if (firstNameCompare !== 0) {
        return firstNameCompare;
      }
      
      // If both lastName and firstName are equal, sort by middleNames
      return (a.middleNames || '').localeCompare(b.middleNames || '');
    });
  }

  getFlaggedContactHeaderRecords(sourceId: string): Observable<FlaggedContactHeaderModel[]> {
    if (environment.useMockData) {
      const mockRecords = MockDataHelper.getMockFlaggedContacts();
      return of(this.sortHeaderRecords(mockRecords));
    }
    
    return this.sdk.getFlaggedContactsAsync(sourceId).pipe(
      map((sdkModels: FlaggedContactModel[]) => {
        if (!sdkModels || !Array.isArray(sdkModels)) {
          return [];
        }
        const mapped = sdkModels.map(sdkModel => this.mapFlaggedContactToHeader(sdkModel));
        return this.sortHeaderRecords(mapped);
      }),
      catchError((error) => {
        const ofacUrl = environment.ofacService.url.endsWith('/') ? environment.ofacService.url : environment.ofacService.url + '/';
        const enhancedError = new Error(
          `API Error: ${error?.message || 'Unknown error'} | Status: ${error?.status || 'N/A'} | URL: ${error?.url || ofacUrl + 'FlaggedContacts/FlaggedContacts/' + sourceId}`
        );
        (enhancedError as any).status = error?.status;
        (enhancedError as any).url = error?.url || ofacUrl + 'FlaggedContacts/FlaggedContacts/' + sourceId;
        return throwError(() => enhancedError);
      })
    );
  }

  private mapFlaggedContactToHeader(sdkModel: FlaggedContactModel | any): FlaggedContactHeaderModel {
    return {
      contactId: this.getProperty(sdkModel, 'contactId', 'ContactId') || '',
      firstName: this.getProperty(sdkModel, 'firstName', 'FirstName') || '',
      lastName: this.getProperty(sdkModel, 'lastName', 'LastName') || '',
      middleNames: this.getProperty(sdkModel, 'middleNames', 'MiddleNames') || '',
      address: this.getProperty(sdkModel, 'address', 'Address') || '',
      city: this.getProperty(sdkModel, 'city', 'City') || '',
      state: this.getProperty(sdkModel, 'state', 'State') || '',
      applicationId: this.getProperty(sdkModel, 'applicationId', 'ApplicationId') || null,
      dateExcluded: this.getProperty(sdkModel, 'dateExcluded', 'DateExcluded'),
      excludedBy: this.getProperty(sdkModel, 'excludedBy', 'ExcludedBy') || ''
    };
  }

  getFlaggedContactSubRecords(sourceId: string, contactId: string): Observable<FlaggedContactSubContactModel[]> {
    if (environment.useMockData) {
      const mockRecords = MockDataHelper.getMockSubContacts(contactId);
      return of(this.sortSubRecords(mockRecords));
    }
    
    return this.sdk.getFlaggedContactDetailsAsync(sourceId, contactId).pipe(
      map((sdkModels: FlaggedContactDetailModel[]) => {
        const mapped = sdkModels.map(sdkModel => this.mapFlaggedContactDetailToSubContact(sdkModel));
        return this.sortSubRecords(mapped);
      }),
      catchError(() => of([]))
    );
  }

  private mapFlaggedContactDetailToSubContact(sdkModel: FlaggedContactDetailModel | any): FlaggedContactSubContactModel {
    const addresses = this.getProperty(sdkModel, 'addresses', 'Addresses') || [];
    const aliases = this.getProperty(sdkModel, 'aliases', 'Aliases') || [];

    return {
      contactId: this.getProperty(sdkModel, 'contactId', 'ContactId') || '',
      firstName: this.getProperty(sdkModel, 'firstName', 'FirstName') || '',
      lastName: this.getProperty(sdkModel, 'lastName', 'LastName') || '',
      middleNames: this.getProperty(sdkModel, 'middleNames', 'MiddleNames') || '',
      ofacContactId: this.getProperty(sdkModel, 'ofacContactId', 'OfacContactId') || '',
      exclusionId: this.getProperty(sdkModel, 'exclusionId', 'ExclusionId') || '',
      applicationId: this.getProperty(sdkModel, 'applicationId', 'ApplicationId') || null,
      addresses: Array.isArray(addresses) ? addresses.map((addr: any) => ({
        address: this.getProperty(addr, 'address', 'Address') || '',
        city: this.getProperty(addr, 'cityState', 'CityState', 'city', 'City') || ''
      })) : [],
      aliases: Array.isArray(aliases) ? aliases.map((alias: any) => ({
        firstName: this.getProperty(alias, 'firstName', 'FirstName') || '',
        lastName: this.getProperty(alias, 'lastName', 'LastName') || '',
        middleNames: this.getProperty(alias, 'middleNames', 'MiddleNames') || ''
      })) : [],
      audit: this.getProperty(sdkModel, 'audit', 'Audit') || null
    };
  }

  getExclusionHeaderRecords(sourceId: string): Observable<FlaggedContactHeaderModel[]> {
    if (environment.useMockData) {
      return of([]);
    }
    
    return this.sdk.getFlaggedContactExclusionsAsync(sourceId).pipe(
      map((sdkModels: FlaggedContactModel[]) => {
        if (!sdkModels || !Array.isArray(sdkModels)) {
          return [];
        }
        const mapped = sdkModels.map(sdkModel => this.mapFlaggedContactToHeader(sdkModel));
        return this.sortHeaderRecords(mapped);
      }),
      catchError(() => of([]))
    );
  }

  getExclusionSubRecords(sourceId: string, contactId: string): Observable<FlaggedContactSubContactModel[]> {
    if (environment.useMockData) {
      // For exclusions, return empty array or filtered mock data
      return of([]);
    }
    
    return this.sdk.getExcludedContactDetailsAsync(sourceId, contactId).pipe(
      map((sdkModels: FlaggedContactDetailModel[]) => {
        const mapped = sdkModels.map(sdkModel => this.mapFlaggedContactDetailToSubContact(sdkModel));
        return this.sortSubRecords(mapped);
      }),
      catchError(() => of([]))
    );
  }

  getAuditsWithApplicationNames(sourceId: string): Observable<AuditModel[]> {
    if (environment.useMockData) {
      return of(MockDataHelper.getMockAuditRecords(sourceId));
    }
    
    return this.sdk.getAssociationAuditDetailsAsync(sourceId).pipe(
      map((sdkModels: AuditDetailModel[]) => {
        return sdkModels
          .map(sdkModel => this.mapAuditDetailToAudit(sdkModel))
          .sort((a, b) => {
            // Sort by descending date (newest first)
            const dateA = a.createdDate?.getTime() || 0;
            const dateB = b.createdDate?.getTime() || 0;
            return dateB - dateA;
          });
      }),
      catchError(() => of([]))
    );
  }

  private mapAuditDetailToAudit(sdkModel: AuditDetailModel): AuditModel {
    let createdDate: Date | null = null;
    if (sdkModel.createdOn) {
      createdDate = sdkModel.createdOn instanceof Date 
        ? sdkModel.createdOn 
        : new Date(sdkModel.createdOn);
    }

    // Convert OfacContactId from string to number if possible
    let ofacContactId: number | null = null;
    if (sdkModel.ofacContactId) {
      const parsed = parseInt(sdkModel.ofacContactId, 10);
      if (!isNaN(parsed)) {
        ofacContactId = parsed;
      }
    }

    return {
      contactId: sdkModel.contactId,
      contactName: sdkModel.contactName,
      createdDate: createdDate,
      createdBy: sdkModel.createdBy,
      ofacContactId: ofacContactId,
      ofacMatchTypeId: null, // AuditDetailModel doesn't have matchTypeId, only matchTypeName
      ofacMatchTypeName: sdkModel.ofacMatchTypeName,
      resultJson: sdkModel.resultJson,
      searchJson: sdkModel.searchJson,
      sourceId: null, // AuditDetailModel doesn't have sourceId
      applicationName: sdkModel.applicationName,
      confidence: sdkModel.confidencePercent
    };
  }

  createExclusion(subRecord: FlaggedContactSubContactModel): Observable<void> {
    if (environment.useMockData) {
      // Mock: just return success
      return of(void 0);
    }
    // TODO: Replace with actual API call using SDK
    return of(void 0);
  }

  removeExclusion(exclusionId: string): Observable<void> {
    if (environment.useMockData) {
      // Mock: just return success
      return of(void 0);
    }
    
    return this.sdk.deleteExclusionAsync(exclusionId).pipe(
      catchError((error) => {
        const enhancedError = new Error(
          `API Error: ${error?.message || 'Unknown error'} | Status: ${error?.status || 'N/A'}`
        );
        (enhancedError as any).status = error?.status;
        return throwError(() => enhancedError);
      })
    );
  }

  logExclusionInclusion(
    sourceId: string,
    contactId: string,
    contactName: string,
    ofacContactId: number | null,
    exclusionId: string
  ): Observable<OfacAuditModel> {
    if (environment.useMockData) {
      // Mock: return a mock audit record
      const mockAudit: OfacAuditModel = {
        ofacAuditId: `MOCK-${Date.now()}`,
        sourceId: sourceId,
        contactId: contactId,
        contactName: contactName,
        ofacContactId: ofacContactId,
        createdOn: new Date(),
        createdBy: 'System',
        resultJson: JSON.stringify({ action: 'ExclusionIncluded', exclusionId: exclusionId })
      };
      return of(mockAudit);
    }

    // Generate a unique audit ID
    const auditId = `EXCL-INCL-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    const auditModel: OfacAuditModel = {
      ofacAuditId: auditId,
      sourceId: sourceId,
      contactId: contactId,
      contactName: contactName,
      ofacContactId: ofacContactId,
      createdOn: new Date(),
      createdBy: 'System', // TODO: Get actual user from auth context
      resultJson: JSON.stringify({ 
        action: 'ExclusionIncluded', 
        exclusionId: exclusionId,
        timestamp: new Date().toISOString()
      })
    };

    return this.sdk.logOfacMatchAsync(auditModel).pipe(
      catchError(() => of(auditModel))
    );
  }

  /**
   * Generates a GUID (UUID v4) for exclusion IDs
   */
  private generateGuid(): string {
    // Use crypto.randomUUID() if available (modern browsers)
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    
    // Fallback: Generate GUID manually
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  addExclusion(
    sourceId: string,
    contactId: string,
    ofacContactId: string | number,
    applicationId?: string | null
  ): Observable<OfacExclusionModel> {
    if (environment.useMockData) {
      // Mock: return a mock exclusion with GUID
      const mockExclusion: OfacExclusionModel = {
        ofacExclusionId: this.generateGuid(),
        sourceId: sourceId,
        contactId: contactId,
        ofacContactId: typeof ofacContactId === 'string' ? parseInt(ofacContactId, 10) : ofacContactId,
        applicationId: applicationId || null,
        excludedOn: new Date(),
        excludedBy: 'System'
      };
      return of(mockExclusion);
    }

    // Generate a GUID for the exclusion ID
    const exclusionId = this.generateGuid();
    
    // Convert ofacContactId to number if it's a string
    let ofacContactIdNum: number | null = null;
    if (ofacContactId) {
      if (typeof ofacContactId === 'string') {
        const parsed = parseInt(ofacContactId, 10);
        if (!isNaN(parsed)) {
          ofacContactIdNum = parsed;
        }
      } else {
        ofacContactIdNum = ofacContactId;
      }
    }

    const exclusionModel: OfacExclusionModel = {
      ofacExclusionId: exclusionId,
      sourceId: sourceId,
      contactId: contactId,
      ofacContactId: ofacContactIdNum,
      applicationId: applicationId || null,
      excludedOn: new Date(),
      excludedBy: 'System' // TODO: Get actual user from auth context
    };

    return this.sdk.addExclusionAsync(exclusionModel).pipe(
      catchError((error) => {
        const enhancedError = new Error(
          `API Error: ${error?.message || 'Unknown error'} | Status: ${error?.status || 'N/A'}`
        );
        (enhancedError as any).status = error?.status;
        return throwError(() => enhancedError);
      })
    );
  }

  sendOfacStatusChangeMessage(
    sourceId: string,
    contactId: string,
    isMatch: boolean
  ): Observable<void> {
    if (environment.useMockData) {
      return of(void 0);
    }

    const model: ServiceBusModel = {
      acaNumber: sourceId,
      contactDbId: contactId,
      correlationId: this.generateGuid(),
      isMatch: isMatch
    };

    return this.sdk.sendOfacStatusChangeMessageAsync(model).pipe(
      catchError((error) => {
        const enhancedError = new Error(
          `API Error: ${error?.message || 'Unknown error'} | Status: ${error?.status || 'N/A'}`
        );
        (enhancedError as any).status = error?.status;
        return throwError(() => enhancedError);
      })
    );
  }
}

